import { useCallback, useEffect, useRef, useState } from 'react';
import { type CabUpdateResponse, getCabUpdates } from '@/api';

export interface UseCabUpdatesPollingOptions {
	/** Booking ID to fetch cab updates for */
	bookingId: string;
	/** Whether the trip is currently active */
	isTripActive: boolean;
	/** Polling interval in milliseconds */
	pollingIntervalMs: number;
}

export const useCabUpdatesPolling = (options: UseCabUpdatesPollingOptions) => {
	const { bookingId, isTripActive, pollingIntervalMs } = options;

	const [cabUpdate, setCabUpdate] = useState<CabUpdateResponse | null>(null);
	const [isPolling, setIsPolling] = useState(false);

	// Refs for cleanup and stable callbacks
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const isPollingRef = useRef(isPolling);

	// Keep ref updated
	isPollingRef.current = isPolling;

	const stopPolling = useCallback(() => {
		// Cancel any in-flight request
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
		// Clear scheduled timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setIsPolling(false);
	}, []);

	const fetchCabUpdates = useCallback(async () => {
		// Cancel previous request if still pending
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Create new AbortController for this request
		abortControllerRef.current = new AbortController();
		const response = await getCabUpdates(bookingId, {
			signal: abortControllerRef.current.signal,
		});
		// Clear the controller after request completion
		abortControllerRef.current = null;

		if (!('errors' in response)) {
			const cabUpdateData = response.data;
			setCabUpdate(cabUpdateData);
			// Stop polling if requested by API
			if (cabUpdateData.stopPolling) {
				stopPolling();
				return false; // Indicate polling should stop
			}
		}

		return true; // Indicate polling should continue
	}, [bookingId, stopPolling]);

	const scheduleNextPoll = useCallback(() => {
		if (!isPollingRef.current) return;

		timeoutRef.current = setTimeout(async () => {
			if (!isPollingRef.current) return;
			const shouldContinue = await fetchCabUpdates();
			// Schedule next poll if should continue and still polling
			if (shouldContinue && isPollingRef.current) {
				scheduleNextPoll();
			}
		}, pollingIntervalMs);
	}, [fetchCabUpdates, pollingIntervalMs]);

	// Start polling with immediate first call
	const startPolling = useCallback(async () => {
		// Prevent double calls - check if already polling
		if (timeoutRef.current || isPollingRef.current) {
			return; // Already polling
		}

		setIsPolling(true);
		// Make immediate first call
		const shouldContinue = await fetchCabUpdates();
		// Schedule subsequent polls if should continue
		if (shouldContinue && isPollingRef.current) {
			scheduleNextPoll();
		}
	}, [fetchCabUpdates, scheduleNextPoll]);

	// Handle trip activation changes
	useEffect(() => {
		if (isTripActive) {
			startPolling();
		} else {
			stopPolling();
			setCabUpdate(null);
		}

		return stopPolling;
	}, [isTripActive, startPolling, stopPolling]);

	return {
		cabUpdate,
		isPolling,
	};
};
